/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Page } from "@/components/Layout";
import Seo from "@/components/Seo";
import LegalToc from "@/components/LegalToc";

/**
 * Refund and Cancellation Policy, replaces the old "Return & Refund" placeholder.
 * Layout mirrors the FAQ page hero (lime gradient + serif title) for visual consistency.
 * Section headings are rendered in Title Case for readability.
 */

const SECTIONS = [
  {
    id: "preamble",
    title: "About This Policy",
    body: (
      <>
        <p>
          The website <strong>www.minddhara.com</strong> and all associated applications, products,
          and services ("Platform") are operated by <strong>MindDhara Private Limited</strong>{" "}
          (CIN: U86909HR2025PTC136572), having its registered office at Flat No. 102, Tower 25,
          Palm Hills, Sector 77, Narsinghpur, Gurugram, Haryana – 122004 (hereinafter referred to
          as "MindDhara", "We", "Us", or "Our"). The terms "You", "Your", and "Yourself" refer to
          the User, meaning any person who registers on, accesses, or uses the Platform or any of
          the Services.
        </p>
        <p>
          This Refund and Cancellation Policy ("Policy") forms part of and must be read together
          with MindDhara's Terms and Conditions, Privacy Policy, and all other Policy Documents
          published on the Platform from time to time. Capitalised terms not defined herein have
          the meanings given to them in the Terms and Conditions. By booking a Session or
          purchasing a Package or Digital Products through the Platform, You confirm that You have
          read, understood, and accepted this Policy.
        </p>
      </>
    ),
  },
  {
    id: "payment",
    title: "Payment",
    body: (
      <>
        <p>
          All Sessions and Packages must be paid for in full before a booking is confirmed. A
          booking is not confirmed until payment is successfully processed. All purchases are
          personal to the User and are non-transferable. Session credits have no cash value, are
          non-transferable, and may not be sold, gifted, or exchanged for cash under any
          circumstances.
        </p>
        <p>
          Payments are processed through Razorpay and such other payment processors as MindDhara
          may engage from time to time. MindDhara is not responsible for failures or delays
          attributable to the payment processor or the User's bank or card issuer.
        </p>
        <p>
          MindDhara does not issue cash refunds. Where a refund is expressly provided for under
          this Policy, the amount will be credited to the original payment method used at the time
          of purchase whether UPI, debit or credit card, or digital wallet. Refunds are ordinarily
          processed by MindDhara within 5 to 7 working days of approval; your bank or card issuer
          may require additional time for the amount to reflect in your account.
        </p>
        <p>
          All purchased Sessions and credits are valid for the period specified at the time of
          purchase. Sessions not used within the applicable validity period are permanently
          forfeited with no refund, except as provided under the Compassionate Exceptions clause of
          this Policy.
        </p>
      </>
    ),
  },
  {
    id: "rescheduling",
    title: "Rescheduling",
    body: (
      <>
        <p>
          MindDhara operates a rescheduling-first model. Sessions cannot be cancelled by the User
          in exchange for a refund. The rescheduling provisions below apply to all Sessions under
          MindDhara Couch and MindDhara Forward.
        </p>
        <h3>Rescheduling By The User</h3>
        <ul>
          <li>
            A User may reschedule a confirmed Session free of charge provided the request is
            submitted at least <strong>12 hours before</strong> the scheduled start time.
          </li>
          <li>
            Requests to reschedule submitted less than 12 hours before the scheduled start time are
            at the sole discretion of the Practitioner or Mentor. The Practitioner or Mentor may
            agree to reschedule or may treat the Session as having been used. MindDhara will not
            intervene in this decision.
          </li>
          <li>Each individual booking may be rescheduled a maximum of two (2) times.</li>
          <li>
            After the second reschedule, the Session is treated as used regardless of attendance
            and no further rescheduling, credit, or refund is available in respect of that booking.
          </li>
          <li>Rescheduled Sessions must fall within the original Package validity period.</li>
        </ul>
        <h3>Rescheduling By Practitioner, Mentor, Or MindDhara</h3>
        <ul>
          <li>
            Where a Practitioner or Mentor reschedules or cancels a confirmed Session, the User
            receives a session credit equivalent to one Session, valid for six (6) months.
          </li>
          <li>
            Where the reschedule or cancellation is made with less than 12 hours' notice, the User
            additionally receives priority rebooking at no additional charge.
          </li>
          <li>
            Where MindDhara itself reschedules a Session due to a platform-level issue, the
            rescheduled Session does not count toward the User's two-reschedule limit.
          </li>
          <li>
            Practitioners or Mentors who cancel on three or more occasions within any 90-day period
            are subject to review and may be removed from the Platform.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "no-shows",
    title: "No-Shows And Late Arrival",
    body: (
      <>
        <p>
          Sessions begin at the scheduled time. Where a User arrives late, the Session ends at its
          originally scheduled time. The missed portion is not compensated, credited, or refunded.
        </p>
        <p>
          A no-show, where the User does not attend and does not communicate before the session
          start time, is treated as a Session used. No credit and no refund are issued.
        </p>
        <p>
          Where a User believes a no-show was caused by a confirmed Platform or Google Meet outage
          affecting both parties, the User must email{" "}
          <a href="mailto:hello@minddhara.com">hello@minddhara.com</a> within 24 hours. If the
          outage is confirmed, MindDhara will issue a session credit.
        </p>
        <p>
          Where a Practitioner or Mentor has not joined within 10 minutes of the scheduled start
          time, the User should contact hello@minddhara.com immediately. MindDhara will treat this
          as a Practitioner-initiated cancellation and issue a session credit valid for six (6)
          months.
        </p>
      </>
    ),
  },
  {
    id: "package-validity",
    title: "Package Validity And Expiry",
    body: (
      <>
        <p>
          All purchased Sessions expire at the end of the Package validity period and cannot be
          refunded. MindDhara will send a reminder communication 14 days before a Package is due to
          expire.
        </p>
        <h3>Validity Periods</h3>
        <ul>
          <li>
            <strong>The First Mile (4 sessions):</strong> 10 weeks from the date of the first
            session; unused sessions expire on day 70.
          </li>
          <li>
            <strong>The Crossing (8 sessions):</strong> 16 weeks from the date of the first
            session; unused sessions expire on day 112.
          </li>
          <li>
            <strong>The Horizon (12 sessions + support):</strong> 12 months from the date of
            purchase.
          </li>
          <li>
            <strong>6-week Couch programme:</strong> 6 weeks from the date of the first session;
            unused sessions expire on day 42.
          </li>
          <li>
            <strong>3-month Couch programme:</strong> 3 months from the date of the first session;
            unused sessions expire on day 90.
          </li>
          <li>
            <strong>6-month Couch programme:</strong> 6 months from the date of the first session;
            unused sessions expire on day 180.
          </li>
          <li>
            <strong>Monthly retainer:</strong> sessions valid for the calendar month of purchase
            only; unused sessions do not roll over.
          </li>
          <li>
            <strong>Forward advisory packages:</strong> validity communicated at the time of
            purchase.
          </li>
          <li>
            <strong>Session credits (all types):</strong> valid for six (6) months from the date of
            issue unless otherwise stated.
          </li>
        </ul>
        <h3>Package Pause</h3>
        <p>
          A User may request a one-time Package pause of up to 14 days for documented medical or
          compassionate reasons by emailing hello@minddhara.com before the pause period begins. An
          approved pause extends the validity period by the number of days paused.
        </p>
        <h3>Grace Period Extension</h3>
        <p>
          Where a User has unused Sessions remaining at the point of Package expiry, the User may
          apply for a 30-day extension by paying a flat fee of <strong>₹1,500 (INR)</strong> or{" "}
          <strong>USD 18</strong>, as applicable. This is available once per Package only and must
          be requested within seven (7) calendar days of expiry.
        </p>
      </>
    ),
  },
  {
    id: "compassionate-exceptions",
    title: "Compassionate Exceptions",
    body: (
      <>
        <p>
          MindDhara does not issue cash refunds. In exceptional and documented circumstances, a
          session credit valid for six (6) months may be issued at MindDhara's sole discretion.
          Eligible circumstances are limited to the following:
        </p>
        <ul>
          <li>
            Bereavement of an immediate family member (parent, sibling, spouse, or child);
          </li>
          <li>Medical hospitalisation of the User or their primary caregiver;</li>
          <li>Acute mental health crisis requiring inpatient care; or</li>
          <li>Natural disaster or declared national emergency affecting the User's region.</li>
        </ul>
        <p>
          Compassionate exceptions will not be granted for foreseeable events, including
          examinations, travel plans, or work or professional commitments. To request an exception,
          the User must email hello@minddhara.com within seven (7) days of the affected Session
          with a brief description and supporting documentation (for example, a hospital letter or
          death certificate). MindDhara will respond within five (5) business days.
        </p>
        <h3>Mental Health Crisis Protocol</h3>
        <p>
          Where a User or their guardian contacts MindDhara and indicates an acute mental health
          emergency, MindDhara affiliates will immediately provide crisis resources and will not
          apply commercial policy to that interaction. Session credits for Sessions affected by a
          documented mental health crisis are issued without question. Crisis helplines:{" "}
          <strong>iCall:</strong> 9152987821; <strong>Tele-MANAS:</strong> 1800-891-4416
          (toll-free).
        </p>
      </>
    ),
  },
  {
    id: "cancellation-by-minddhara",
    title: "Cancellation By MindDhara Or Practitioner / Mentor",
    body: (
      <p>
        Where MindDhara or a Practitioner or Mentor cancels a confirmed Session, the User will
        receive a session credit valid for six (6) months, usable with any Practitioner or Mentor
        on the Platform. Where MindDhara discontinues a Service already purchased by a User,
        MindDhara will provide a session credit or such other remedy as it considers appropriate.
        Where a Practitioner or Mentor is removed from the Platform, affected Users will be offered
        a rebooking with an alternative Practitioner or Mentor or a full session credit valid for
        six (6) months, and must indicate their preference within seven (7) days of notification.
      </p>
    ),
  },
  {
    id: "technical-issues",
    title: "Technical Issues During A Session",
    body: (
      <>
        <p>
          Sessions are conducted via Google Meet. Users are responsible for testing their setup
          before their first Session; MindDhara will provide a pre-session checklist on booking.
        </p>
        <p>
          Where a Session cannot be completed due to a confirmed technical failure attributable to
          the Platform or the Practitioner or Mentor, the Session will be rescheduled at no cost
          and will not count toward the User's two-reschedule limit.
        </p>
        <p>
          Where a Session is disrupted due to the User's own connectivity or device failure, the
          Session is treated as used. No automatic credit or refund is issued.
        </p>
        <p>
          Where a User disputes whether a Session was completed, the User must email
          hello@minddhara.com within 24 hours of the session end time. MindDhara will respond
          within one to five (1–5) business days.
        </p>
      </>
    ),
  },
  {
    id: "minddhara-forward",
    title: "MindDhara Forward",
    body: (
      <p>
        <strong>Psychology Skills Development and Mentorship.</strong> All provisions of this
        Policy apply equally to MindDhara Forward. Forward packages, mentorship sessions, document
        reviews, mock interviews, workshop bookings, and any other deliverable provided through
        MindDhara Forward are non-refundable once delivered. No cancellation window or cooling-off
        period applies to any Forward service. Advisory packages are non-refundable and no
        cancellation window or cooling-off period applies. Once an advisory Session or
        deliverable, including Statement of Purpose review, mock interview, or document review,
        has been delivered by a Mentor, no credit or refund is available in respect of that
        Session or deliverable. The validity period and pause and grace period provisions of the
        Package Validity clause apply to Forward packages.
      </p>
    ),
  },
  {
    id: "non-refundable-situations",
    title: "Non-Refundable Situations",
    body: (
      <>
        <p>No credit or refund is available in any of the following circumstances:</p>
        <ul>
          <li>
            The User's Account has been suspended or terminated for breach of the Terms and
            Conditions or any other Policy Document;
          </li>
          <li>Sessions or credits have expired in accordance with the applicable validity period;</li>
          <li>The Session or deliverable has been attended or completed;</li>
          <li>
            The circumstance was foreseeable at the time of booking, including examinations,
            scheduled travel, or work commitments; or
          </li>
          <li>
            The Session or credit was provided as a complimentary or promotional benefit and
            carries no cash value.
          </li>
        </ul>
        <p>
          MindDhara will honour session credits and rescheduling rights for Topmate bookings in
          accordance with this Policy to the extent operationally practicable. Users with Topmate
          booking disputes are encouraged to contact hello@minddhara.com in the first instance.
        </p>
      </>
    ),
  },
  {
    id: "chargebacks",
    title: "Chargebacks And Payment Disputes",
    body: (
      <p>
        Users who have a genuine concern about a charge are encouraged to contact
        hello@minddhara.com before initiating a chargeback. Most matters are resolved within five
        (5) business days. Where a chargeback is initiated, MindDhara will respond with
        documentation of the service provided and this Policy. Fraudulent or vexatious chargebacks
        may result in Account suspension.
      </p>
    ),
  },
  {
    id: "complaints-process",
    title: "Complaints Process",
    body: (
      <p>
        Where a User is dissatisfied with the quality of a Session or deliverable for reasons
        unrelated to cancellation or non-attendance, the User may submit a formal complaint to
        MindDhara's Grievance Officer at hello@minddhara.com. Complaints will be handled in
        accordance with MindDhara's grievance redressal process as set out in the Terms and
        Conditions. Quality complaints do not automatically entitle a User to a session credit or
        refund but will be reviewed on their merits.
      </p>
    ),
  },
  {
    id: "free-consultations",
    title: "Free Consultations",
    body: (
      <p>
        Free consultations are complimentary and carry no cash value. A no-show to a free
        consultation forfeits the entitlement. MindDhara reserves the right to limit the
        availability of free consultations at its discretion.
      </p>
    ),
  },
  {
    id: "updates",
    title: "Updates To This Policy",
    body: (
      <p>
        MindDhara reviews this Policy periodically and may update it at any time. Material changes
        will be communicated by email at least fourteen (14) days before they take effect. The
        version of this Policy in force at the time of a booking or request shall govern that
        booking or request. Continued use of MindDhara's Services after the effective date of any
        update constitutes acceptance of the revised Policy.
      </p>
    ),
  },
];

export default function RefundCancellationPolicy() {
  return (
    <Page>
      <Seo
        title="Refund and Cancellation Policy"
        description="MindDhara's Refund and Cancellation Policy, payment, rescheduling, no-show, package validity, compassionate exceptions and complaints."
      />

      {/* Hero, standard dark-green centered md-page-hero */}
      <section className="md-section md-page-hero" data-testid="refund-hero">
        <div className="md-container text-center">
          <div className="md-label-eyebrow mb-3">Legal · Policies</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif" data-testid="refund-hero-title">
            Refund and cancellation <em>policy</em>
          </h1>
          <p className="text-base mt-5 max-w-2xl mx-auto">
            How payment, rescheduling, no-shows, package validity and compassionate exceptions work
            at MindDhara.
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="md-section" data-testid="refund-body">
        <div className="md-container">
          <div className="md-legal-layout">
            <article className="md-legal-article md-legal-article-flat" data-testid="refund-article">
              {SECTIONS.map((s) => (
                <section key={s.id} id={s.id} className="md-legal-section">
                  <h2 className="md-legal-h2" data-testid={`refund-h2-${s.id}`}>
                    {s.title}
                  </h2>
                  <div className="md-legal-body">{s.body}</div>
                </section>
              ))}
            </article>
            <LegalToc items={SECTIONS.map((s) => ({ id: s.id, label: s.title }))} testid="legal-toc-refund" />
          </div>
        </div>
      </section>
    </Page>
  );
}
